/**
 * events/CoreEventBus.ts — the system-wide event bus at the heart of Roamer Core™.
 *
 * Engines never call each other. They publish events here; Roamer Core routes
 * them to every interested subscriber. This is the loose-coupling backbone:
 * adding a future engine is "subscribe + publish", never a direct call.
 *
 * LIVE EVENT-DRIVEN PLATFORM sprint additions (build 1.88.0):
 * - Subscriber identity: on()/onAny() accept an optional subscriberId so the
 *   Developer Center™ can show WHO received an event (previously private).
 * - Per-delivery instrumentation: execution time per subscriber, error
 *   capture (broken subscribers still never break the bus), slow-subscriber
 *   detection, publish→dispatch latency, duplicate-publish detection, and a
 *   bounded delivery log (publisher → subscriber → timing → result).
 * - All additions are additive: the pre-sprint API and stats fields are
 *   unchanged, so existing callers and tests keep working.
 */

import { eventContractRegistry } from './EventContractRegistry';

export type CoreEventName =
  | 'LocationChanged'
  | 'WeatherUpdated'
  | 'CurrencyUpdated'
  | 'HousingUpdated'
  | 'PlacesUpdated'
  | 'EventsUpdated'
  | 'SafetyUpdated'
  | 'CommunityUpdated'
  | 'MissionCreated'
  | 'MissionCompleted'
  | 'PaymentReceived'
  | 'JourneyUpdated'
  | 'RoamerScoreUpdated'
  | 'PhotoUploaded'
  | 'HeaderChanged'
  | 'NotificationSent'
  | 'EngineRegistered'
  | 'EngineStatusChanged'
  | 'SystemStateChanged'
  | 'WorkflowCompleted'
  | 'CareerUpdated'
  | 'IncomeRecorded'
  | 'IncomeUpdated'
  | 'CompanyAdded'
  | 'CompanyUpdated'
  | 'DataSynchronized'
  | 'PersistenceFailed'
  | 'OperatorSignedIn'
  | 'OperatorSignedOut'
  | 'SessionRestored'
  | 'SessionExpired'
  | 'OperatorHydrated'
  | 'CloudHydrationCompleted';

/** Runtime enumeration of the CoreEventName union (kept in lockstep). */
export const ALL_CORE_EVENT_NAMES: readonly CoreEventName[] = [
  'LocationChanged', 'WeatherUpdated', 'CurrencyUpdated', 'HousingUpdated',
  'PlacesUpdated', 'EventsUpdated', 'SafetyUpdated', 'CommunityUpdated',
  'MissionCreated', 'MissionCompleted', 'PaymentReceived', 'JourneyUpdated',
  'RoamerScoreUpdated', 'PhotoUploaded', 'HeaderChanged', 'NotificationSent',
  'EngineRegistered', 'EngineStatusChanged', 'SystemStateChanged', 'WorkflowCompleted',
  'CareerUpdated', 'IncomeRecorded', 'IncomeUpdated', 'CompanyAdded', 'CompanyUpdated',
  'DataSynchronized', 'PersistenceFailed',
  'OperatorSignedIn', 'OperatorSignedOut', 'SessionRestored', 'SessionExpired',
  'OperatorHydrated', 'CloudHydrationCompleted',
];

export interface CoreEvent<T = unknown> {
  name: CoreEventName;
  /** Which engine published it ('core' for the orchestrator itself). */
  source: string;
  at: number;
  payload: T;
}

export type CoreListener = (event: CoreEvent) => void;

export interface EventBusStats {
  published: number;
  delivered: number;
  /** Events published while a dispatch was already in flight (queued, not lost). */
  queuedPeak: number;
  byName: Partial<Record<CoreEventName, number>>;
  // ── Live-platform instrumentation (additive) ──
  /** Deliveries whose subscriber threw (caught; bus never breaks). */
  deliveryErrors: number;
  /** Deliveries slower than SLOW_DELIVERY_MS. */
  slowDeliveries: number;
  /** Same name+source+payload published within DUPLICATE_WINDOW_MS. */
  duplicateEvents: number;
  /** Events lost. The queue is unbounded, so this stays 0 by design — tracked to prove it. */
  droppedEvents: number;
  /** Rolling average publish→dispatch latency (ms) over the delivery log. */
  avgLatencyMs: number;
  maxLatencyMs: number;
}

/** One subscriber receiving one event — the Dev Center timeline row. */
export interface DeliveryRecord {
  event: CoreEventName;
  publisher: string;
  subscriber: string;
  at: number;          // event.at (publish time)
  latencyMs: number;   // publish → dispatch start
  execMs: number;      // subscriber handler execution time
  ok: boolean;
  error?: string;
}

/** Aggregate per-subscriber performance for the slow-subscriber panel. */
export interface SubscriberStats {
  subscriber: string;
  deliveries: number;
  errors: number;
  totalExecMs: number;
  maxExecMs: number;
  avgExecMs: number;
  slow: boolean;
}

const HISTORY_LIMIT = 100;
const DELIVERY_LOG_LIMIT = 200;
const SLOW_DELIVERY_MS = 8;        // one 120fps frame — anything slower is flagged
const DUPLICATE_WINDOW_MS = 250;   // identical publishes inside this window count as dupes
const WINDOW_EVENT_PREFIX = 'nomad:core:';

interface Subscriber {
  fn: CoreListener;
  id: string;
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Bounded duplicate-detection key. Serializing arbitrary payloads on the hot
 * publish path is O(payload size), so the walk carries a strict character
 * budget: small payloads get a stable key; anything that exceeds the budget
 * (or can't be serialized) returns null and is simply exempt from duplicate
 * detection — publish latency is never amplified by payload size.
 */
const DUP_KEY_BUDGET = 512;

function duplicateKey(payload: unknown): string | null {
  let remaining = DUP_KEY_BUDGET;
  const parts: string[] = [];
  const write = (s: string): boolean => {
    remaining -= s.length;
    if (remaining < 0) return false;
    parts.push(s);
    return true;
  };
  const walk = (value: unknown, depth: number): boolean => {
    if (depth > 4) return false; // deep payloads → exempt, not expensive
    if (value === null || value === undefined) return write(String(value));
    const t = typeof value;
    if (t === 'string') return (value as string).length <= remaining && write(`"${value}"`);
    if (t === 'number' || t === 'boolean') return write(String(value));
    if (t === 'function' || t === 'symbol' || t === 'bigint') return false;
    if (Array.isArray(value)) {
      if (value.length > 16 || !write('[')) return false;
      for (const item of value) if (!walk(item, depth + 1) || !write(',')) return false;
      return write(']');
    }
    const keys = Object.keys(value as object);
    if (keys.length > 16 || !write('{')) return false;
    for (const k of keys) {
      if (!write(`${k}:`) || !walk((value as Record<string, unknown>)[k], depth + 1) || !write(',')) return false;
    }
    return write('}');
  };
  return walk(payload, 0) ? parts.join('') : null;
}

export class CoreEventBus {
  private listeners = new Map<CoreEventName, Set<Subscriber>>();
  private anyListeners = new Set<Subscriber>();
  private history: CoreEvent[] = [];
  private stats: EventBusStats = {
    published: 0, delivered: 0, queuedPeak: 0, byName: {},
    deliveryErrors: 0, slowDeliveries: 0, duplicateEvents: 0, droppedEvents: 0,
    avgLatencyMs: 0, maxLatencyMs: 0,
  };
  private deliveryLog: DeliveryRecord[] = [];
  private subscriberAgg = new Map<string, { deliveries: number; errors: number; totalExecMs: number; maxExecMs: number }>();
  private latencySum = 0;
  private latencyCount = 0;
  private lastPublishKey: string | null = '';
  private lastPublishAt = 0;
  private anonCounter = 0;

  // Re-entrancy queue: events published from inside a handler are dispatched
  // in order after the current one finishes (no interleaved handler stacks).
  private queue: Array<CoreEvent & { enqueuedAtMs: number }> = [];
  private dispatching = false;

  on(name: CoreEventName, listener: CoreListener, subscriberId?: string): () => void {
    let set = this.listeners.get(name);
    if (!set) {
      set = new Set();
      this.listeners.set(name, set);
    }
    const sub: Subscriber = { fn: listener, id: subscriberId ?? `anonymous#${++this.anonCounter}` };
    set.add(sub);
    return () => set!.delete(sub);
  }

  onAny(listener: CoreListener, subscriberId?: string): () => void {
    const sub: Subscriber = { fn: listener, id: subscriberId ?? `anonymous#${++this.anonCounter}` };
    this.anyListeners.add(sub);
    return () => this.anyListeners.delete(sub);
  }

  publish<T>(name: CoreEventName, source: string, payload: T, at: number): void {
    // EVENT CONTRACT SYSTEM™ (1.89.0): validate against the registered
    // contract. No-op until the registry is armed (bootstrap registers the
    // contracts), so a bare bus behaves exactly as before. Never throws,
    // never blocks the publish — dev warns loudly, production records only.
    eventContractRegistry.validateForPublish(name, source, payload, at);

    // Duplicate detection: identical name+source+payload inside the window.
    // Budget-bounded key: oversized/unserializable payloads are exempt (null),
    // never expensive — see duplicateKey().
    const payloadKey = duplicateKey(payload);
    const key = payloadKey === null ? null : `${name}|${source}|${payloadKey}`;
    if (key !== null && key === this.lastPublishKey && at - this.lastPublishAt <= DUPLICATE_WINDOW_MS) {
      this.stats.duplicateEvents += 1;
    }
    this.lastPublishKey = key ?? '';
    this.lastPublishAt = at;

    this.queue.push({ name, source, at, payload, enqueuedAtMs: nowMs() });
    this.stats.queuedPeak = Math.max(this.stats.queuedPeak, this.queue.length);
    if (this.dispatching) return; // current dispatch loop will drain it
    this.dispatching = true;
    try {
      while (this.queue.length > 0) {
        const queued = this.queue.shift()!;
        const { enqueuedAtMs, ...event } = queued;
        this.record(event);
        this.dispatch(event, nowMs() - enqueuedAtMs);
      }
    } finally {
      this.dispatching = false;
    }
  }

  private dispatch(event: CoreEvent, latencyMs: number): void {
    this.latencySum += latencyMs;
    this.latencyCount += 1;
    this.stats.avgLatencyMs = this.latencySum / this.latencyCount;
    this.stats.maxLatencyMs = Math.max(this.stats.maxLatencyMs, latencyMs);

    const targets = [
      ...(this.listeners.get(event.name) ?? []),
      ...this.anyListeners,
    ];
    for (const sub of targets) {
      const start = nowMs();
      let ok = true;
      let error: string | undefined;
      try {
        sub.fn(event);
      } catch (err) {
        // A broken subscriber must never break the bus or other subscribers.
        ok = false;
        error = err instanceof Error ? err.message : String(err);
        this.stats.deliveryErrors += 1;
      }
      const execMs = nowMs() - start;
      this.stats.delivered += 1;
      if (execMs > SLOW_DELIVERY_MS) this.stats.slowDeliveries += 1;
      this.recordDelivery({
        event: event.name,
        publisher: event.source,
        subscriber: sub.id,
        at: event.at,
        latencyMs: Math.round(latencyMs * 100) / 100,
        execMs: Math.round(execMs * 100) / 100,
        ok,
        ...(error !== undefined ? { error } : {}),
      });
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(`${WINDOW_EVENT_PREFIX}${event.name}`, { detail: event }));
    }
  }

  private recordDelivery(rec: DeliveryRecord): void {
    this.deliveryLog.push(rec);
    if (this.deliveryLog.length > DELIVERY_LOG_LIMIT) this.deliveryLog.shift();
    let agg = this.subscriberAgg.get(rec.subscriber);
    if (!agg) {
      agg = { deliveries: 0, errors: 0, totalExecMs: 0, maxExecMs: 0 };
      this.subscriberAgg.set(rec.subscriber, agg);
    }
    agg.deliveries += 1;
    if (!rec.ok) agg.errors += 1;
    agg.totalExecMs += rec.execMs;
    agg.maxExecMs = Math.max(agg.maxExecMs, rec.execMs);
  }

  private record(event: CoreEvent): void {
    this.stats.published += 1;
    this.stats.byName[event.name] = (this.stats.byName[event.name] ?? 0) + 1;
    this.history.push(event);
    if (this.history.length > HISTORY_LIMIT) this.history.shift();
  }

  getStats(): EventBusStats {
    return { ...this.stats, byName: { ...this.stats.byName } };
  }

  getHistory(): CoreEvent[] {
    return [...this.history];
  }

  /** Recent deliveries (publisher → subscriber, timing, result) — newest last. */
  getDeliveryLog(): DeliveryRecord[] {
    return [...this.deliveryLog];
  }

  /** Per-subscriber aggregates, slowest first. */
  getSubscriberStats(): SubscriberStats[] {
    const out: SubscriberStats[] = [];
    for (const [subscriber, agg] of this.subscriberAgg) {
      const avgExecMs = agg.deliveries > 0 ? agg.totalExecMs / agg.deliveries : 0;
      out.push({
        subscriber,
        deliveries: agg.deliveries,
        errors: agg.errors,
        totalExecMs: Math.round(agg.totalExecMs * 100) / 100,
        maxExecMs: Math.round(agg.maxExecMs * 100) / 100,
        avgExecMs: Math.round(avgExecMs * 100) / 100,
        slow: agg.maxExecMs > SLOW_DELIVERY_MS,
      });
    }
    return out.sort((a, b) => b.avgExecMs - a.avgExecMs);
  }

  get pendingCount(): number {
    return this.queue.length;
  }
}
